import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Heading, Spinner, Text } from '@chakra-ui/react';
import { FiUploadCloud } from 'react-icons/fi';
import { Link, useHistory, useLocation } from 'react-router-dom';

import {
  AppliedFeedReference,
  FeedAnalysisAlerts,
  feedAnalysisService,
  FeedSample,
  SummaryRow,
} from '../../../../services/feedAnalysisService';
import { AlertsList } from '../components/AlertsList';
import { AppliedReferencesTable } from '../components/AppliedReferencesTable';
import { KpiCards } from '../components/KpiCards';
import { MonthSelector } from '../components/MonthSelector';
import { ResultsGrid } from '../components/ResultsGrid';
import { currentMonth, monthInput, monthLong, shiftMonth } from '../format';
import { PageShell } from '../PageShell';

interface MonthData {
  samples: FeedSample[];
  alerts: FeedAnalysisAlerts;
  references: AppliedFeedReference[];
  summary: SummaryRow;
}

const FeedAnalysisDashboard: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const queryMonth = new URLSearchParams(location.search).get('month');
  const [month, setMonth] = useState<string | null>(queryMonth);
  const [data, setData] = useState<MonthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // sem ?month, abre no último mês com amostra (até 12 meses para trás)
  useEffect(() => {
    if (queryMonth) {
      setMonth(queryMonth);
      return;
    }
    const to = currentMonth();
    feedAnalysisService
      .indicators({ from: shiftMonth(to, -11), to })
      .then(indicators => {
        const last = [...indicators.months]
          .reverse()
          .find(item => item.samples > 0);
        setMonth(last ? monthInput(last.month) : to);
      })
      .catch(() => setMonth(to));
  }, [queryMonth]);

  const load = useCallback(async (selected: string) => {
    setLoading(true);
    setError('');
    try {
      const [samples, alerts, references, indicators] = await Promise.all([
        feedAnalysisService.listSamples({ month: selected }),
        feedAnalysisService.alerts(selected),
        feedAnalysisService.appliedReferences(selected),
        feedAnalysisService.indicators({ from: selected, to: selected }),
      ]);
      setData({ samples, alerts, references, summary: indicators.totals });
    } catch {
      setError('Não foi possível carregar as análises do mês.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (month) load(month);
  }, [month, load]);

  const changeMonth = (value: string) => {
    history.replace(`/feed-analysis?month=${value}`);
    setMonth(value);
  };

  // referências só das rações analisadas no mês, como na planilha
  const usedReferences = useMemo(() => {
    if (!data) return [];
    const sampled = new Set(data.samples.map(item => item.feed_id));
    return data.references.filter(item => sampled.has(item.feed_id));
  }, [data]);

  const actions = (
    <>
      {month && <MonthSelector value={month} onChange={changeMonth} />}
      <Button
        as={Link}
        to="/feed-analysis/import"
        leftIcon={<FiUploadCloud />}
        colorScheme="blue"
        size="sm"
      >
        Importar laudos
      </Button>
    </>
  );

  return (
    <PageShell
      title="Resultados de Análises de Ração"
      subtitle="Laudos do laboratório comparados com a referência do nutricionista vigente no mês."
      actions={actions}
    >
      {(loading || !month) && (
        <Flex justify="center" py="10">
          <Spinner />
        </Flex>
      )}

      {!loading && error && <Text color="red.400">{error}</Text>}

      {!loading && !error && data && month && (
        <>
          <Text color="gray.300" fontWeight="600" mb="3">
            {monthLong(`${month}-01`)}
          </Text>
          <KpiCards summary={data.summary} />

          <Box mb="8">
            <Flex align="baseline" justify="space-between" mb="3" gridGap="2">
              <Heading size="md" fontWeight="600">
                Alertas
              </Heading>
              {data.alerts.pending_review > 0 && (
                <Text fontSize="sm" color="yellow.300">
                  {data.alerts.pending_review} amostra(s) fora da faixa sem
                  parecer do analista
                </Text>
              )}
            </Flex>
            <AlertsList alerts={data.alerts.alerts} limit={8} />
          </Box>

          <Heading size="md" fontWeight="600" mb="3">
            Resultados do mês
          </Heading>
          {data.samples.length ? (
            <ResultsGrid samples={data.samples} />
          ) : (
            <Box
              bg="gray.800"
              borderWidth="1px"
              borderColor="gray.700"
              borderRadius="lg"
              p="8"
              textAlign="center"
            >
              <Text color="gray.400" mb="4">
                Nenhuma amostra importada neste mês.
              </Text>
              <Button
                as={Link}
                to="/feed-analysis/import"
                leftIcon={<FiUploadCloud />}
                colorScheme="blue"
              >
                Importar laudos
              </Button>
            </Box>
          )}

          {usedReferences.length > 0 && (
            <Box mt="8">
              <Heading size="md" fontWeight="600" mb="1">
                Valores de referência aplicados neste mês
              </Heading>
              <Text fontSize="sm" color="gray.400" mb="3">
                Faixa = esperado ± tolerância. Origem: cadastro de referências
                do nutricionista.
              </Text>
              <AppliedReferencesTable references={usedReferences} />
            </Box>
          )}
        </>
      )}
    </PageShell>
  );
};

export default FeedAnalysisDashboard;
